import { useContext, useEffect, useState } from 'react';
import clsx from 'clsx';
import { AText, Box } from '@alf/uikit';
import { SiteSettingsContext } from '@namespace/cms';
import { useDeviceStore } from '@namespace/device';
import { useI18n } from '@namespace/i18n';
import { Icon } from '@namespace/icons2';
import { HTMLSnippet } from '@namespace/snippets';
import { useGetImage } from '@namespace/themes';
import { UserContext } from '@namespace/user';
import {
  PROMO_TIMER_TYPES,
  PROMOTION_ITEM_TYPE_TO_PROMO_TYPE
} from '../../../constants';
import getPromoImage from '../../../utils/getPromoImage';
import { BonusTakePartButton } from '../../BonusTakePartButton';
import DateTime from '../../DateTime';
import Tags from '../../Tags';
import { BonusDetailsButton } from './BonusDetailsButton';

import styles from './index.module.css';

export const BonusItem = ({ bonus, togglePromoItem, promoItemActiveId }) => {
  const [{ siteConfig, promoTags }] = useContext(SiteSettingsContext);
  const [user] = useContext(UserContext);
  const { realPageType, isMobile } = useDeviceStore();
  const { activeWallet = {} } = user;
  const { currency: userCurrency = null } = activeWallet;
  const { defaultCurrency } = siteConfig;
  const currentCurrency = userCurrency || defaultCurrency;
  const [isError, setIsError] = useState(false);
  const { t, language, f } = useI18n();
  const getImg = useGetImage();

  const {
    tags,
    type,
    bonusSourceType,
    bonusId,
    promoId,
    showFrom,
    periodEnd,
    periodStart,
    isBonusActive,
    isBonusFinished,
    bonusTemplateId,
    titleTranslationKey,
    settingsPerCurrency,
    showTimerInsteadPeriod,
    defaultDescriptionSnippetId,
    defaultDescriptionTranslationKey,
    system = ''
  } = bonus;
  const [currentBonusId, setCurrentBonusId] = useState(bonusId);
  const [isShowTakePart, setIsShowTakePart] = useState(!isBonusActive);

  const isShowMoreOpened = promoId === promoItemActiveId;
  const { defaultImageLink, defaultImageLinkDark, imagesPerLanguage } =
    bonus[realPageType] || {};

  const [isParticipateAdded, setIsParticipateAdded] = useState(false);
  const defaultPromoImage = getImg(defaultImageLink, defaultImageLinkDark);

  const [currentScheduleTimerType, setCurrentScheduleTimerType] = useState();
  const currentDate = f.getDateTime();
  const periodStartDate = f.getDateTime({ date: periodStart });

  const isDisableTakePart =
    currentDate < periodStartDate ||
    currentScheduleTimerType === PROMO_TIMER_TYPES.BEGIN ||
    currentScheduleTimerType === PROMO_TIMER_TYPES.AVAILABLE_AGAIN;

  const onSuccessTakePart = (bonusInstanceId) => {
    setIsParticipateAdded(true);

    if (bonusInstanceId) {
      setCurrentBonusId(bonusInstanceId);
      setIsShowTakePart(false);
    }
  };

  const promoImage = getPromoImage(
    settingsPerCurrency,
    imagesPerLanguage,
    language,
    realPageType,
    currentCurrency,
    getImg
  );

  useEffect(() => {
    setIsShowTakePart(!isBonusActive);
    setCurrentBonusId(bonusId);
  }, [isBonusActive, bonusId]);

  if (isMobile) {
    return (
      <Box
        className={clsx([
          styles.bonusItemContainer,
          isShowMoreOpened && styles.openedCard
        ])}
        data-role="promo-bonus-item"
        align="center"
        direction="column"
      >
        <Box className={styles.mobileBonusWrapper}>
          <Box direction="column" className={styles.bonusDescriptionWrapper}>
            <Tags
              className={styles.tags}
              tags={tags}
              promoTagsConfig={promoTags}
              showFrom={showFrom}
            />
            <Box className={styles.bonusTitle} justiify="start">
              <AText
                breed="BU"
                size="l"
                color="txt_color_3"
                data-role="promo-bonus-item-title"
              >
                {t(titleTranslationKey, promoId)}
              </AText>
            </Box>
            <Box
              onClick={() => togglePromoItem(promoId)}
              className={styles.bonusDescription}
            >
              {!isShowMoreOpened && (
                <AText
                  breed="R"
                  size="m"
                  color="txt_color_7"
                  data-role="promo-bonus-item-description"
                >
                  {t(defaultDescriptionTranslationKey, promoId)}
                  <AText
                    data-role="bonus-item-moreinfo-btn"
                    className={styles.showMoreBtn}
                    color="txt_link"
                    size="m"
                  >
                    {t(`web.bonuses.button.moreinfo`)}
                  </AText>
                </AText>
              )}
              <HTMLSnippet
                className={clsx([
                  styles.snippet,
                  !isShowMoreOpened && styles.snippetHide // should be hide with css because we need to load snippet on page load
                ])}
                snippetId={defaultDescriptionSnippetId}
              />
            </Box>
          </Box>
          <Box>
            <img
              src={
                (isError ? defaultPromoImage : promoImage) || defaultPromoImage
              }
              className={clsx([styles.bonusImage])}
              onError={() => setIsError(true)}
              alt="bonusImage"
              data-role="promo-bonus-item-image"
            />
          </Box>
        </Box>
        <Box direction="column" className={styles.bottomBlock}>
          <Box
            data-role="promo-bonus-item-datetime"
            align="center"
            className={clsx([
              styles.dateTimeBlock,
              isBonusFinished && styles.finishedBonus
            ])}
          >
            {(isShowTakePart && !isParticipateAdded) || isBonusFinished ? (
              <DateTime
                showTimerInsteadPeriod={showTimerInsteadPeriod}
                periodStart={periodStart}
                periodEnd={periodEnd}
                dateTextColor="po_txt_color_1"
                dateTextSize="xl"
                activePromoType={PROMOTION_ITEM_TYPE_TO_PROMO_TYPE[type]}
                promoData={bonus}
                onScheduleTimerTypeChange={setCurrentScheduleTimerType}
              />
            ) : (
              <Box
                data-role="promo-bonus-item-youarein"
                padding="m 0 s"
                align="center"
              >
                <Icon
                  name="icons/general/state/success"
                  size="s"
                  className={styles.icon}
                  color="accent_color_5"
                />
                <AText breed="B" size="l" color="state_success">
                  {t('web.bonuses.flag.youarein')}
                </AText>
              </Box>
            )}
          </Box>
          {!isBonusFinished && (
            <Box
              className={clsx([
                styles.buttonWrapper,
                !isDisableTakePart && styles.pointer
              ])}
            >
              {isShowTakePart && !isParticipateAdded ? (
                <BonusTakePartButton
                  bonusSource={system.toLowerCase()}
                  bonusTemplateId={bonusTemplateId}
                  onSuccessTakePart={onSuccessTakePart}
                  isDisable={isDisableTakePart}
                />
              ) : (
                <BonusDetailsButton
                  bonusId={currentBonusId}
                  bonusSourceType={bonusSourceType}
                  isShowTakePart={isShowTakePart}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className={clsx([
        styles.bonusItemContainer,
        isShowMoreOpened && styles.openedCard
      ])}
      data-role="promo-bonus-item"
      align="center"
    >
      <Box direction="column" className={styles.bonusDescriptionWrapper}>
        <Tags
          className={styles.tags}
          tags={tags}
          promoTagsConfig={promoTags}
          showFrom={showFrom}
        />
        <Box className={styles.bonusTitle} justiify="start">
          <AText
            breed="BU"
            size="l"
            color="txt_color_3"
            data-role="promo-bonus-item-title"
          >
            {t(titleTranslationKey, promoId)}
          </AText>
        </Box>
        <Box
          onClick={() => togglePromoItem(promoId)}
          className={styles.bonusDescription}
        >
          {!isShowMoreOpened && (
            <AText
              breed="R"
              size="m"
              color="txt_color_7"
              data-role="promo-bonus-item-description"
            >
              {t(defaultDescriptionTranslationKey, promoId)}
              <AText
                data-role="bonus-item-moreinfo-btn"
                className={styles.showMoreBtn}
                color="txt_link"
                size="m"
              >
                {t(`web.bonuses.button.moreinfo`)}
              </AText>
            </AText>
          )}
          <HTMLSnippet
            className={clsx([
              styles.snippet,
              !isShowMoreOpened && styles.snippetHide // should be hide with css because we need to load snippet on page load
            ])}
            snippetId={defaultDescriptionSnippetId}
          />
        </Box>
        <Box
          data-role="promo-bonus-item-datetime"
          className={clsx([
            isBonusFinished ? styles.finishedBonus : styles.dateTimeBlock
          ])}
        >
          {(isShowTakePart && !isParticipateAdded) || isBonusFinished ? (
            <DateTime
              showTimerInsteadPeriod={showTimerInsteadPeriod}
              periodStart={periodStart}
              periodEnd={periodEnd}
              dateTextColor="po_txt_color_1"
              dateTextSize="xl"
              activePromoType={PROMOTION_ITEM_TYPE_TO_PROMO_TYPE[type]}
              promoData={bonus}
              onScheduleTimerTypeChange={setCurrentScheduleTimerType}
            />
          ) : (
            <Box
              data-role="promo-bonus-item-youarein"
              padding="s 0"
              align="center"
            >
              <Icon
                name="icons/general/state/success"
                size="s"
                className={styles.icon}
                color="accent_color_5"
              />
              <AText breed="B" size="l" color="state_success">
                {t('web.bonuses.flag.youarein')}
              </AText>
            </Box>
          )}
        </Box>
        {!isBonusFinished && (
          <Box className={styles.buttonWrapper}>
            {isShowTakePart && !isParticipateAdded ? (
              <BonusTakePartButton
                bonusSource={system.toLowerCase()}
                bonusTemplateId={bonusTemplateId}
                onSuccessTakePart={onSuccessTakePart}
                isDisable={isDisableTakePart}
              />
            ) : (
              <BonusDetailsButton
                bonusId={currentBonusId}
                bonusSourceType={bonusSourceType}
                isShowTakePart={isShowTakePart}
              />
            )}
          </Box>
        )}
      </Box>
      <Box>
        <img
          src={(isError ? defaultPromoImage : promoImage) || defaultPromoImage}
          className={clsx([styles.bonusImage])}
          onError={() => setIsError(true)}
          alt="bonusImage"
          data-role="promo-bonus-item-image"
        />
      </Box>
    </Box>
  );
};
